import * as React from "react";
import { cn } from "@/shared/utils/cn";

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  onValueChange?: (value: string) => void;
}

export interface SelectOptionProps
  extends React.OptionHTMLAttributes<HTMLOptionElement> {}

/**
 * Select Component
 * 
 * Standardized select dropdown with consistent styling
 */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, children, onValueChange, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onValueChange) {
        onValueChange(e.target.value);
      }
      if (props.onChange) {
        props.onChange(e);
      }
    };

    return (
      <select
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={ref}
        onChange={handleChange}
        {...props}
      >
        {children}
      </select>
    );
  }
);
Select.displayName = "Select";

const SelectOption = React.forwardRef<HTMLOptionElement, SelectOptionProps>(
  ({ className, ...props }, ref) => {
    return <option className={cn(className)} ref={ref} {...props} />;
  }
);
SelectOption.displayName = "SelectOption";

// Aliases for compatibility with other UI libraries
const SelectContent = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const SelectItem = SelectOption;
const SelectTrigger = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div className={cn("relative", className)} ref={ref} {...props}>
      {children}
    </div>
  )
);
const SelectValue = ({ placeholder }: { placeholder?: string }) => <>{placeholder}</>;

export { 
  Select, 
  SelectOption,
  // Export aliases for compatibility
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
};
